import { PaymentAPI } from '@cheerfy/lib/js/payments/paymentApi';
import { I18N } from '@cheerfy/lib/js/i18n';
import { EventDispatcher } from '../events/event-dispatcher';
import { IPage } from '../shared/interfaces/pages/page.interface';
import { Currency } from '../integrations/locale/currency/currency';
import {
    EmbeddedPayResponse,
    ProcessPaymentResponse,
    PayResponse,
} from '../shared/types/backend/pay-response.type';
import {
    AdyenConfiguration,
    StripeConfiguration,
    PaycometConfiguration,
    PaymentMethodEngineConfiguration,
    CashConfiguration,
} from '../shared/types/backend/payment-method-configuration.type';
import { Status } from '../shared/enums/status.enum';
import { URL } from '../integrations/url/url';
import { FormComponent } from '../components/cheerfypay/form/form.component';
import { AnalyticsAdapter } from '@cheerfy/lib/reactive/features/api/models/analytics';
import { getAdapter } from '@cheerfy/lib/reactive/features/api/adapter';
import { GoogleAnalytics } from '@cheerfy/lib/js/analytics/google';
import { FacebookAnalytics } from '@cheerfy/lib/js/analytics/facebook';

export class Page extends EventDispatcher implements IPage {
    public static isUserPath(): boolean {
        return window.location.pathname.includes('/process-order/user/');
    }

    public static getPaymentSessionTokenFromUrl() {
        if (!Page.isUserPath()) {
            return undefined;
        }

        // Find the segment after 'user' in the URL path
        const pathSegments = window.location.pathname.split('/');
        const userIndex = pathSegments.indexOf('user');

        if (userIndex === -1) {
            return undefined;
        }

        return pathSegments[userIndex + 1];
    }

    public static isTokenizationAvailable() {
        const payResponse = Page.getPayResponseOrFail<EmbeddedPayResponse>();

        if (!payResponse.tokenization) {
            return false;
        }

        return payResponse.tokenization === Status.ENABLED;
    }

    public static getPayResponse<R extends PayResponse>(): Extract<PayResponse, R> | undefined {
        return <Extract<PayResponse, R>>(window.__PAY_RESPONSE__);
    }

    public static getPayResponseOrFail<R extends PayResponse = PayResponse>(): R {
        const response = Page.getPayResponse<R>();

        if (response === undefined) {
            throw new Error('Cannot get pay response');
        }

        return response;
    }

    public static getEngineFromPayResponse<T extends PaymentMethodEngineConfiguration<PaycometConfiguration,
        StripeConfiguration, AdyenConfiguration, CashConfiguration>,
        K extends keyof T>(
        key: K,
        configuration: {
                engine: PaymentAPI.PaymentMethodEngine,
                method: PaymentAPI.PaymentMethodType,
            },
    ): T[K] {
        const payResponse = Page.getPayResponseOrFail();

        const embeddedPayResponse = <Partial<EmbeddedPayResponse>>payResponse;
        const backendPayResponse = <Partial<ProcessPaymentResponse>>payResponse;

        const backendPaymentMethodConfiguration = backendPayResponse.payment_methods?.find(
            (c) => c.method_type === configuration.method && c.engine === configuration.engine,
        );

        if (backendPaymentMethodConfiguration !== undefined) {
            return (<T>backendPaymentMethodConfiguration.configuration)[key];
        }

        if (embeddedPayResponse.engines !== undefined) {
            return (<T>embeddedPayResponse.engines)[key];
        }

        throw new Error('Cannot find engine configuration');
    }

    public static parseToIntElements() {
        $('[data-to-int]')
            .toArray()
            .forEach((item) => {
                const { toInt } = item.dataset;

                if (toInt === undefined) {
                    return;
                }

                const number = Number(item.innerText);

                if (Number.isNaN(number)) {
                    return;
                }

                // eslint-disable-next-line no-param-reassign
                item.innerText = number.toFixed();
            });
    }

    private static parseScreenHeight() {
        const elements = $('[data-screenHeight]')
            .toArray();

        const calculate = () => {
            elements
                .forEach((e) => {
                    e.style.height = `${window.innerHeight}px`;
                });
        };

        window.addEventListener('resize', () => calculate());
        calculate();
    }

    public constructor() {
        super();

        $.validator.methods.number = function v(value: string, element: any) {
            return this.optional(element) || /^-?(?:\d+|\d{1,3}(?:[\s.,]\d{3})+)(?:[.,]\d+)?$/.test(value);
        };

        Page.parseScreenHeight();

        this._loadAnalytics();
        this._mountCookies();
    }

    public getLanguageData(data: any, lang: string = 'en'): any {
        const texts = data?.settings?.cookies?.text?.[lang];
        return {
            consentModal: {
                title: texts?.title || '',
                description: texts?.content || '',
                acceptAllBtn: texts?.btn_accept || '',
                acceptNecessaryBtn: texts?.btn_accept_necessary || '',
                showPreferencesBtn: texts?.btn_admin_cookies || '',
            },
            preferencesModal: {
                title: texts?.btn_admin_cookies || '',
                acceptAllBtn: texts?.btn_accept || '',
                acceptNecessaryBtn: texts?.btn_accept_necessary || '',
                savePreferencesBtn: texts?.btn_accept_seleccion || '',
            },
        };
    }

    private _loadAnalytics(){
        const payResponse = Page.getPayResponse();

        if(!payResponse?.analytics_configuration){
            return;
        }

        const model = getAdapter(AnalyticsAdapter).adapt(payResponse.analytics_configuration);
        const activeCodes = model.getCodes();

        if(activeCodes.ga){
            GoogleAnalytics.loadGa(activeCodes.ga)
        }

        if(activeCodes.gtm){
            GoogleAnalytics.loadGTM(activeCodes.gtm)
        }

        if(activeCodes.pixel){
            this._loadPixelOnMarketingConsent(activeCodes.pixel);
        }
    }

    // The pixel is opt-in: it loads only once the cookie banner reports marketing
    // consent. Without the banner (skipped domains/locations, user paths) no
    // consent event ever fires, so the pixel is not loaded and fbq is left alone
    // for any pixel the customer inserts on their own.
    private _loadPixelOnMarketingConsent(pixel: string) {
        let loaded = false;

        const update = () => {
            if (window.CookieConsent?.acceptedCategory?.('marketing')) {
                loaded = true;
                FacebookAnalytics.grantConsent();
                FacebookAnalytics.loadPixel(pixel);

                return;
            }

            // The script cannot be unloaded, so withdrawn consent is revoked on it
            if (loaded) {
                FacebookAnalytics.revokeConsent();
            }
        };

        // vanilla-cookieconsent (loaded by cookiesV2.js) dispatches these on window
        // when consent is restored from its cookie, given or changed
        window.addEventListener('cc:onConsent', update);
        window.addEventListener('cc:onChange', update);

        update();
    }

    private _mountCookies() {
        if (!window?.CookiesManagement || Page.isUserPath()) {
            return;
        }

        const payResponse = Page.getPayResponseOrFail();
        const [urlLocId] = URL.getUrlData();

        const locId = (payResponse as ProcessPaymentResponse).location_id ?? urlLocId;

        const skippedLocations: string[] = [
            ...(FormComponent.isProd() ? [
                // Prod locations
            ] : [
                // Beta locations
                '593', // Mahou demobar
            ]),
        ];

        if (skippedLocations.includes(locId.toString())) {
            return;
        }

        const lang = I18N.getPriorityLanguage(
            navigator?.languages?.map((l) => l.split('-')[0]) ?? [],
            payResponse?.pay_configuration?.settings?.configuration?.supported_languages ?? [],
        );

        const {
            colors,
            font_family: fontFamily,
        } = payResponse?.pay_configuration?.design ?? { colors: {}, font_family: '' };

        window.CookiesManagement?.init({
            lang,
            translations: this.getLanguageData(payResponse?.pay_configuration, lang),
            isMaster: true,
            style: {
                font: fontFamily ?? '"Quicksand", serif;',
                colors: {
                    bgColor: colors?.main_section_background ?? '#fff',
                    textColor: colors?.main_section_text ?? '#000',
                    primaryBtnBgColor: colors?.main_button_background ?? '#000',
                    primaryBtnTextColor: colors?.main_button_text ?? '#fff',
                    secondaryBtnBgColor: colors?.secondary_button_background ?? '#000',
                    secondaryBtnTextColor: colors?.secondary_button_text ?? '#fff',
                    highlightHighlight: colors?.main_section_highlight ?? '#000',
                },
            },
        });
    }

    public async render(): Promise<Page> {
        Currency.parseHtmlCurrencies();
        Page.parseToIntElements();

        return this;
    }
}
